import { Primitive } from '@/types/Primitive'
import { Operator } from 'helpers/generic/search'
import { cloneDeep } from 'lodash'

export function getIdFromLocationHeader(location: string): string {
  return (location || '').split('/').pop() ?? ''
}

export type SortOrder = 'ASC' | 'DESC'

const handlePrefixedSearch = (query: PageQuery): PageQuery => {
  if (!query.search) return query
  const searchKeys = Object.keys(query.search)
  searchKeys.forEach((key: string) => {
    if (key.startsWith('ca_search_') && query.search && typeof query.search !== 'string') {
      query[key] = query.search[key]
      delete query.search[key]
    }
    if (key.startsWith('query_search_') && query.search && typeof query.search !== 'string') {
      query[key.replace('query_search_', '')] = query.search[key]
      delete query.search[key]
    }
  })
  return query
}

export const isOne = (value?: string | number | null): boolean => value?.toString() === '1'
export const parsedNotificationKey = (key: string) => {
  if (key.includes('NotificationType')) return 'NotificationType'
  return key
}

export const buildSearchQuery = (
  searchQuery?: SearchQuery | null,
  operator: 'OR' | 'AND' = 'AND',
  isNested = false
): string | null | undefined => {
  if (!searchQuery) {
    return null
  }

  const paramsFiltered = Object.entries(searchQuery).filter(([key, value]) => !!key && !!value)
  const params: (string | undefined)[] = paramsFiltered.map(([key, value]) => {
    if (key === 'nested')
      return (value as NestedSearchQuery[])
        .map((nestedQuery) => buildSearchQuery(nestedQuery.queries, nestedQuery.operator, true))
        .join(` ${operator} `)
    if (typeof value === 'string' || typeof value === 'number')
      return `(${parsedNotificationKey(key)} == ${value})`
    if (value && 'operator' in value)
      return `(${parsedNotificationKey(key)} ${value?.operator} ${value?.value})`
    return undefined
  })

  for (const key in params) {
    if (params.length - 1 > parseInt(key, 10)) params[key] = `${params[key]} ${operator}`
  }

  if (isNested) return `(${params.join(' ')})`
  const isPostNested = !!params[0] && !params[0][0].includes('OR') && !params[0][0].includes('AND')
  if (isOne(params.length) && isPostNested)
    return params.join(' ').replace('(', '').replace(')', '')
  return params.join(' ')
}

export const sortingQueryBuilder = (sort?: string | null): string | null => {
  if (!sort) {
    return null
  }
  return `${sort}`
}
export const preparePageQueryParams = (query?: PageQuery): URLSearchParams => {
  const cloneQuery = cloneDeep(query)
  const newQuery = handlePrefixedSearch(cloneQuery || {})
  const searchParams: PageQuery = {
    ...newQuery,
    search:
      typeof newQuery.search === 'string'
        ? newQuery.search
        : buildSearchQuery(newQuery?.search as SearchQuery | null, newQuery?.operator),
    sort: sortingQueryBuilder(newQuery?.sort) || null,
    pageSize: newQuery?.pageSize ?? 10,
    pageIndex: newQuery?.pageIndex ?? 1,
  }

  delete searchParams.sortOrder
  delete searchParams.operator

  const params = Object.entries(searchParams)
    .filter(([key, value]) => {
      return !!key && value !== null && value !== undefined
    })
    .reduce<[string, any][]>((acc, [key, value]) => {
      if (key.startsWith('ca_search') && typeof value === 'string') {
        const values = value.split(/[\s,]+/)
        // Ensure each item is a tuple of [string, any]
        const newProps = values.map((val: string): [string, any] => [key, val])
        return [...acc, ...newProps]
      } else {
        return [...acc, [key, value]]
      }
    }, [])

  return new URLSearchParams(params)
}

export const prepareQueryParams = (
  queryParamsObject: Record<string, Primitive>
): URLSearchParams => {
  // Ensure queryParamsObject is not undefined; if it is, default to an empty object
  const queryParams = queryParamsObject
    ? Object.entries(queryParamsObject)
        .filter(([_, value]) => value !== undefined)
        // Convert all non-undefined values to strings
        .map(([key, value]) => [key, String(value)])
    : []

  // Use URLSearchParams to construct the query string from filtered parameters
  return new URLSearchParams(queryParams)
}

export interface OperativeQuery {
  value: string | number | boolean
  operator: Operator
}

export interface SearchQueryKeys {
  [key: string]: string | number | OperativeQuery | undefined
}

export interface SearchQuery {
  [key: string]: string | NestedSearchQuery[] | number | OperativeQuery | undefined
  nested?: NestedSearchQuery[]
}

export interface NestedSearchQuery {
  queries: SearchQueryKeys
  operator: 'AND' | 'OR'
}

export interface PageQuery {
  search?: SearchQuery | string | null
  sort?: string | null
  pageIndex?: number
  pageSize?: number
  q?: string

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}
